import { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { FiCheck, FiCheckCircle, FiSearch, FiBarChart2, FiEdit3, FiChevronRight } from 'react-icons/fi';
import Modal from './Modal';
import Button from './Button';
import { Select, DateField, inputClass, inputSmClass, labelClass, textareaClass } from './FormControls';
import Chip from './Chip';
import Icon from './Icon';
import { mcaAPI, companiesAPI } from '../../services/api';
import { useApp } from '../../context/AppContext';
import { useUI } from '../../context/UIContext';
import { computeCompanyDeadlines, formatDateEN } from '../../utils/agmDeadlineEngine';
import { resolveMcaFetchRoute, summarizeMcaRefresh } from '../../utils/mcaFetchRoute';
import ResolutionAuthorizationsSection from './ResolutionAuthorizationsSection';
import CompanyProfileSections from './CompanyProfileSections';

const INITIAL_FORM = {
  cin: '',
  registrationNo: '',
  name: '',
  classOfCompany: '',
  category: '',
  subCategory: '',
  incorporated: '',
  status: '',
  activeCompliance: '',
  roc: '',
  rocOffice: '',
  rd: '',
  jurisdiction: '',
  listed: '',
  authorisedCapital: '',
  paidUpCapital: '',
  turnover: '',
  // Tri-state (true / false / null): null defers to the statutory thresholds
  // in the deadline engine's requiresXbrl().
  xbrlApplicable: null,
  indAsApplicable: null,
  subsidiaryOfListed: null,
  lastAgm: '',
  balanceSheetDate: '',
  email: '',
  registeredAddress: '',
  booksAddress: '',
  assignedProfessional: '',
  contactPerson: '',
  auditor: '',
  financialYearEnd: '',
  notes: '',
  type: '',
  city: '',
  score: 85,
  nextDue: '',
  directors: [],
  charges: [],
  // Forms already filed for a past FY. Recorded from the Annual Filing page;
  // carried here so editing a company never wipes them.
  filedForms: [],
  resolutionAuthorizations: {},

  // ── Compliance profile ───────────────────────────────────────────────────
  // The inputs statutory applicability turns on, beyond company type. Rendered
  // by CompanyProfileSections; see that file for what each one decides.
  //
  // Numerics start as '' and are sent as null when left blank — NOT as 0.
  // A stored 0 would read as a genuine zero and could resolve a threshold the
  // wrong way; null reaches the rule engine as "not stated" and yields
  // `cannot determine`.
  netWorthHistory: [],
  netProfitHistory: [],
  borrowings: '',
  publicDeposits: '',
  reservesAndSurplus: '',
  employeeCountHistory: [],
  esiEligibleCountHistory: [],
  statutoryCoverage: [],
  establishments: [],
  nicCode: '',
  sector: '',
  hasDigitalOperations: null,
  processesPersonalData: null,
  foreignShareholdingPercent: '',
  hasEcb: null,
  hasOverseasInvestment: null,
  isHoldingCompany: null,
  isSubsidiaryCompany: null,
  isGovernmentCompany: null,
  governedBySpecialAct: null,
  exchanges: [],
  chairmanIsExecutive: null,
  securitiesInDemat: null,
  pan: '',
  gstRegistrations: [],
  hasInternationalTransactions: null,
  hasSpecifiedDomesticTransactions: null,
  isTdsDeductor: null,
  isDpiitStartup: null,
  dpiitRecognitionNo: ''
};

// Exact MCA Master Data field-name → form field mapping
const MCA_FIELD_MAP = {
  'CIN': 'cin',
  'LLPIN': 'cin',
  'FCRN': 'cin',
  'Company Name': 'name',
  'LLP Name': 'name',
  'ROC Name': 'roc',
  'Registration Number': 'registrationNo',
  'Date of Incorporation': 'incorporated',
  'Email Id': 'email',
  'Registered Address': 'registeredAddress',
  'Address at which the books of account are to be maintained': 'booksAddress',
  'Listed in Stock Exchange(s) (Y/N)': 'listed',
  'Category of Company': 'category',
  'Subcategory of the Company': 'subCategory',
  'Class of Company': 'classOfCompany',
  'ACTIVE compliance': 'activeCompliance',
  'Authorised Capital (Rs)': 'authorisedCapital',
  'Paid up Capital (Rs)': 'paidUpCapital',
  'Date of last AGM': 'lastAgm',
  'Date of Balance Sheet': 'balanceSheetDate',
  'Company Status': 'status',
  'ROC (name and office)': 'rocOffice',
  'RD (name and Region)': 'rd',
  'Jurisdiction': 'jurisdiction',
};

function mapType(category = '', classOfCompany = '', subCategory = '') {
  const text = `${category} ${classOfCompany}`.toLowerCase();
  const sub = (subCategory || '').toLowerCase();
  if (text.includes('llp')) return 'LLP';
  if (text.includes('one person')) return 'OPC';
  // Sub-category–derived types (check before generic class checks)
  if (sub.includes('nidhi')) return 'Nidhi Company';
  if (sub.includes('state govt') || sub.includes('central govt')) return 'Government Company';
  if (text.includes('public')) return 'Public';
  if (text.includes('section 8') || text.includes('guarantee') || text.includes('ngo')) return 'Sec 8';
  if (text.includes('private')) return 'Pvt Ltd';
  return 'Pvt Ltd';
}

// Unescape MCA's obfuscated email: [at] → @, [dot] → .
function unescapeEmail(val) {
  return String(val || '').replace(/\[at\]/g, '@').replace(/\[dot\]/g, '.');
}

// Convert any date value → "YYYY-MM-DD" for <input type="date">.
// Handles: "DD/MM/YYYY", "YYYY-MM-DD", full ISO "2024-09-30T00:00:00.000Z".
function toIso(raw = '') {
  const s = String(raw || '').trim();
  if (!s || s === '-') return '';
  // DD/MM/YYYY → YYYY-MM-DD
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  // Full ISO timestamp "2024-09-30T..." or plain "YYYY-MM-DD" → strip time part
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

// Trash icon shared by both sections
function IconTrash() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 3.5h12M5 3.5V2.25A.75.75 0 0 1 5.75 1.5h2.5A.75.75 0 0 1 9 2.25V3.5M2.5 3.5l.75 9.75A1 1 0 0 0 4.25 14.5h5.5a1 1 0 0 0 1-.75L11.5 3.5M5.5 7v4M8.5 7v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// Parse MCA Master Data XLSX (vertical key-value layout in MasterData sheet)
function parseMcaXlsx(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });

  const sheetName = wb.SheetNames.includes('MasterData')
    ? 'MasterData'
    : wb.SheetNames[0];

  const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1 });

  const raw = {};
  for (const row of rows) {
    const key = String(row[0] || '').trim();
    const val = row[1] !== undefined && row[1] !== null ? String(row[1]).trim() : '';
    if (key && key !== 'Company Information') {
      raw[key] = val;
    }
  }

  const form = {};
  for (const [mcaKey, formField] of Object.entries(MCA_FIELD_MAP)) {
    if (raw[mcaKey] !== undefined && raw[mcaKey] !== '-' && raw[mcaKey] !== '') {
      form[formField] = raw[mcaKey];
    }
  }

  // Uppercase identifiers
  if (form.cin) form.cin = form.cin.toUpperCase();
  if (form.name) form.name = form.name.toUpperCase();

  // Unescape email
  if (form.email) form.email = unescapeEmail(form.email);

  // Normalize listed field from XLSX (could be "Y", "N", "Yes", "No", "Unlisted", etc.)
  if (form.listed !== undefined) {
    const raw_listed = form.listed.trim();
    if (!raw_listed || /^(n|no|unlisted)$/i.test(raw_listed)) form.listed = 'No';
    else if (/BSE\s*[&]\s*NSE|NSE\s*[&]\s*BSE/i.test(raw_listed)) form.listed = 'Yes — BSE & NSE';
    else if (/BSE/i.test(raw_listed)) form.listed = 'Yes — BSE';
    else if (/NSE/i.test(raw_listed)) form.listed = 'Yes — NSE';
    else if (/^(y|yes|listed)$/i.test(raw_listed)) form.listed = 'Yes';
    else form.listed = 'No';
  }

  // Derive company type from category + class + sub-category
  form.type = mapType(raw['Category of Company'] || '', raw['Class of Company'] || '', form.subCategory || '');

  // ── Director Details sheet ──────────────────────────────────────────────────
  form.directors = [];
  const dirSheetName = wb.SheetNames.find(n => /director/i.test(n));
  const dirWs = dirSheetName ? wb.Sheets[dirSheetName] : null;
  if (dirWs) {
    const dirRows = XLSX.utils.sheet_to_json(dirWs, { header: 1 });
    let colNames = null;
    for (const row of dirRows) {
      const nonEmpty = Array.isArray(row) ? row.filter(c => c !== '' && c != null) : [];
      // Header row: multiple cells, contains DIN or Name keyword
      if (!colNames && nonEmpty.length > 1 && nonEmpty.some(c => /DIN|Name/i.test(String(c)))) {
        colNames = row.map(c => String(c ?? '').trim());
        continue;
      }
      if (colNames && nonEmpty.length > 0) {
        const d = {};
        colNames.forEach((k, i) => { d[k] = String(row[i] ?? '').trim(); });
        const din = d['DIN/PAN'] || d['DIN'] || d['PAN'] || '';
        const dirName = d['Name'] || d['Director Name'] || '';
        if (din || dirName) {
          form.directors.push({
            din,
            name: dirName,
            designation: d['Designation'] || '',
            category: d['Category'] || '',
            appointed: toIso(d['Date of Appointment'] || ''),
            cessation: toIso(d['Cessation Date'] || ''),
            signatory: /yes/i.test(d['Signatory'] || ''),
          });
        }
      }
    }
  }

  // ── Index of Charges sheet ──────────────────────────────────────────────────
  // Bug-fix: title row ("Index Of Charges") is a single cell that matches /charge/i
  // so we require > 1 non-empty cells before treating a row as the header.
  form.charges = [];
  const chgSheetName = wb.SheetNames.find(n => /charge/i.test(n));
  const chgWs = chgSheetName ? wb.Sheets[chgSheetName] : null;
  if (chgWs) {
    const chgRows = XLSX.utils.sheet_to_json(chgWs, { header: 1 });
    let colNames = null;
    for (const row of chgRows) {
      const nonEmpty = Array.isArray(row) ? row.filter(c => c !== '' && c != null) : [];
      // Header row: multiple cells, at least one matches charge/srn/holder keyword
      if (!colNames && nonEmpty.length > 1 && nonEmpty.some(c => /charge|srn|holder/i.test(String(c)))) {
        colNames = row.map(c => String(c ?? '').trim());
        continue;
      }
      if (colNames && nonEmpty.length > 0) {
        const c = {};
        colNames.forEach((k, i) => { c[k] = String(row[i] ?? '').trim(); });
        const chargeId = c['Charge Id'] || c['ChargeId'] || c['Charge ID'] || '';
        const srn = c['SRN'] || c['Srn'] || '';
        if (chargeId || srn) {
          const satisfiedRaw = c['Date of Satisfaction'] || c['Satisfaction Date'] || '';
          const satisfiedDate = toIso(satisfiedRaw);
          form.charges.push({
            chargeId,
            srn,
            holderName: c['Charge Holder Name'] || c['Holder Name'] || '',
            created: toIso(c['Date of Creation'] || c['Creation Date'] || ''),
            modified: toIso(c['Date of Modification'] || ''),
            satisfiedDate,
            isSatisfied: Boolean(satisfiedDate && satisfiedRaw !== '-' && satisfiedRaw !== ''),
            amount: c['Amount'] || '',
            // Additional fields for future use (legal docs, charge certificates, etc.)
            address: c['Address'] || '',
            registeredByOtherEntity: c['Whether charge registered by other entity'] || '',
            assetHolderName: c['Asset Holder Name'] || '',
          });
        }
      }
    }
  }

  return form;
}

// "1500000.00" → "1500000". Mirrors trimAmount in backend/services/cinLookupService.js.
const trimAmount = (v) => String(v || '').replace(/\.0+$/, '');

function normalizeFetchData(raw) {
  const record = Array.isArray(raw) ? (raw[0] || {}) : (raw || {});

  function pick(aliases) {
    for (const alias of aliases) {
      const val = record[alias];
      if (val !== undefined && val !== null && String(val).trim() !== '') return String(val).trim();
    }
    return '';
  }

  const category = pick(['CompanyCategory', 'category', 'company_category']);
  const classOfCompany = pick(['CompanyClass', 'companyClass', 'class_of_company']);
  const subCategory = pick(['CompanySubCategory', 'subCategory']);

  return {
    cin: pick(['CIN', 'cin']).toUpperCase(),
    registrationNo: pick(['CompanyRegistrationNumber', 'registrationNo']).toUpperCase(),
    name: pick(['CompanyName', 'companyName', 'company_name']).toUpperCase(),
    classOfCompany,
    category,
    subCategory,
    incorporated: pick(['DateofIncorporation', 'dateOfIncorporation', 'CompanyRegistrationdate_date']),
    status: pick(['CompanyStatus', 'status']),
    activeCompliance: pick(['ACTIVECompliance', 'activeCompliance']),
    roc: pick(['CompanyROCcode', 'rocCode']),
    rocOffice: pick(['CompanyROCoffice', 'rocOffice']),
    rd: pick(['RD', 'rd']),
    jurisdiction: pick(['CompanyStateCode', 'state', 'stateCode']),
    listed: (() => {
      const raw = pick(['ListingStatus', 'Listingstatus', 'listingstatus', 'listed']);
      if (!raw || /^unlisted$/i.test(raw) || /^n$/i.test(raw) || /^no$/i.test(raw)) return 'No';
      if (/BSE\s*[&]\s*NSE|NSE\s*[&]\s*BSE/i.test(raw)) return 'Yes — BSE & NSE';
      if (/BSE/i.test(raw)) return 'Yes — BSE';
      if (/NSE/i.test(raw)) return 'Yes — NSE';
      if (/^(y|yes|listed)$/i.test(raw)) return 'Yes';
      return 'No';
    })(),
    // MCA returns capital with trailing decimals ("1500000.00"). Strip them —
    // the field is a rupee amount, and ".00" in the form just reads as noise.
    authorisedCapital: trimAmount(pick(['AuthorizedCapital', 'authorisedCapital'])),
    paidUpCapital: trimAmount(pick(['PaidupCapital', 'paidUpCapital'])),
    lastAgm: pick(['DateofLastAGM', 'lastAgm']),
    balanceSheetDate: pick(['DateofBalanceSheet', 'balanceSheetDate']),
    email: pick(['EmailId', 'email']),
    registeredAddress: pick(['Registered_Office_Address', 'registeredAddress']),
    booksAddress: pick(['BooksOfAccountsAddress', 'booksAddress']),
    type: mapType(category, classOfCompany, subCategory),
    city: pick(['city']),
  };
}

/**
 * @param {string} [openSection] one of the compliance-profile section keys
 *   (financials / workforce / establishments / operations / structure). Set when
 *   the user arrived from a "fill this in" link on the compliance checklist —
 *   the section opens and scrolls itself into view, so the field they were sent
 *   for is the thing they see.
 */
export default function AddCompanyModal({ open, onClose, initialData, openSection }) {
  const { addOrUpdateCompany, selectCompany, activeCompany, companies, fetchCompanies } = useApp();
  const { showToast } = useUI();
  const uploadInputRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const isEditMode = Boolean(initialData);
  const [saving, setSaving] = useState(false);

  // In edit mode there are no tabs — form is shown directly
  const [activeTab, setActiveTab] = useState('add');
  const [cinQuery, setCinQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  // Collapsible sub-sections. Directors / Charges open by default; the five
  // compliance-profile sections start collapsed so adding a company from MCA
  // data stays a fast path — you fill the profile afterwards, by which time you
  // actually have the head count and the state split to hand.
  const [openSections, setOpenSections] = useState({
    directors: true,
    charges: true,
    financials: false,
    workforce: false,
    establishments: false,
    operations: false,
    structure: false,
  });
  const toggleSection = (k) => setOpenSections((s) => ({ ...s, [k]: !s[k] }));
  const [importedFileName, setImportedFileName] = useState('');
  // Set when the typed identifier is already one of the user's companies, so the
  // fetch must be routed through the whitelisted server-side refresh instead of
  // filling the form and blanket-PUTting it. Holds { company, companyId }.
  const [refreshPrompt, setRefreshPrompt] = useState(null);
  // Guards the one remaining overwrite path: MCA-sourced form content (XLSX
  // import, or a live fetch whose CIN was later edited) saved over a company we
  // already store. Holds the CIN the user has confirmed once.
  const overwriteConfirmedRef = useRef('');
  const [form, setForm] = useState(INITIAL_FORM);
  // Tracks whether the current form content was loaded from an edit, so leftover
  // edit data isn't carried into a fresh "Add Company".
  const formFromEditRef = useRef(false);
  const [resAuthExpanded, setResAuthExpanded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    if (!open) return;
    setActiveTab('add');
    setStatus({ type: '', text: '' });
    setImportedFileName('');
    setCinQuery('');
    setRefreshPrompt(null);
    overwriteConfirmedRef.current = '';
    saveInFlightRef.current = false;
    setSaving(false);
    setResAuthExpanded(false);
    setExpandedCategories({});
    // Edit mode: load the company being edited. Add mode: keep whatever's
    // already in the form so closing a half-filled form and reopening doesn't
    // wipe the user's work. (A successful save resets it; Clear Form clears it.)
    if (initialData) {
      setForm({ ...INITIAL_FORM, ...initialData });
      formFromEditRef.current = true;
    } else if (formFromEditRef.current) {
      // Previous content came from an edit — don't carry it into a fresh add.
      setForm(INITIAL_FORM);
      formFromEditRef.current = false;
    }

    // Arrived from a "fill this in" link: open that section and put it on
    // screen. Opening it silently below the fold looks like the link did
    // nothing, which is how the advice stops being followed.
    if (openSection) {
      setOpenSections((s) => ({ ...s, [openSection]: true }));
      setTimeout(() => {
        document.getElementById(`company-profile-section-${openSection}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }, [open, initialData, openSection]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // Refresh an existing portfolio company from the registry.
  //
  // Server-side by design: `POST /api/companies/:id/mca-refresh` is
  // field-whitelisted and skip-absent, so a field MCA doesn't publish is left
  // alone instead of being blanked, and the `mcaDataAsOf` stamp is written in the
  // same transaction as the fetch. The form is deliberately NOT filled here —
  // filling it would put us one Save away from the blanket PUT this replaces.
  const doMcaRefresh = async () => {
    const target = refreshPrompt;
    if (!target?.companyId) return;
    const label = target.company?.name || target.company?.cin || 'this company';
    setLoading(true);
    setStatus({ type: 'loading', text: `Refreshing ${label} from the MCA registry...` });
    try {
      const response = await companiesAPI.refreshFromMca(target.companyId);
      await fetchCompanies();
      const summary = summarizeMcaRefresh(response, formatDateEN);
      setStatus({ type: 'success', text: summary });
      showToast(summary, 'success');
      setRefreshPrompt(null);
    } catch (error) {
      // Both failure modes (not published / source unreachable) leave the record
      // untouched and unstamped, so say so rather than implying a partial update.
      const msg = error?.message || 'Could not fetch MCA data — nothing was changed.';
      setStatus({
        type: error?.code === 'MCA_SOURCE_UNAVAILABLE' ? 'unavailable' : 'error',
        text: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const doMcaFetch = async () => {
    const query = cinQuery.trim().toUpperCase();
    if (!query) {
      setStatus({ type: 'error', text: 'Enter CIN / LLPIN first.' });
      return;
    }

    // Route the click before touching the registry. An identifier we already
    // store must never come back through fetch-and-fill + blanket PUT — that is
    // what blanked `jurisdiction` and silently rewrote `paidUpCapital`.
    const route = resolveMcaFetchRoute(query, companies);
    if (route.mode === 'blocked') {
      setStatus({
        type: 'error',
        text: route.reason === 'read-only'
          ? `${route.company?.name || query} is shared with you as read-only, so its registry data can't be refreshed. Ask the owner for edit access.`
          : `${query} is already in your portfolio but its record couldn't be identified. Reload the page and try again.`,
      });
      return;
    }
    if (route.mode === 'refresh') {
      // Explain first, write second — the user gets to see what a refresh will
      // and won't touch before anything happens.
      setRefreshPrompt({ company: route.company, companyId: route.companyId });
      setStatus({ type: '', text: '' });
      return;
    }

    setRefreshPrompt(null);
    setLoading(true);
    setStatus({ type: 'loading', text: 'Connecting to MCA registry...' });
    try {
      const response = await mcaAPI.fetchCompanyByCinOrLlpin(query);
      // Backend signals dataset retired
      if (response?.code === 'MCA_SOURCE_UNAVAILABLE' || !response?.success) {
        setStatus({ type: 'unavailable', text: response?.message || 'MCA data source unavailable.' });
        return;
      }
      const fetched = normalizeFetchData(response?.data);
      if (!fetched.cin || !fetched.name) throw new Error('Could not find complete company details for this CIN.');
      setForm((prev) => ({ ...prev, ...fetched, resolutionAuthorizations: {} }));
      setActiveTab('add');
      setStatus({ type: 'success', text: 'Company data fetched and auto-filled.' });
      setImportedFileName('MCA live fetch');
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Unable to fetch company data.';
      const code = error?.response?.data?.code;
      if (code === 'MCA_SOURCE_UNAVAILABLE') {
        setStatus({ type: 'unavailable', text: msg });
      } else {
        setStatus({ type: 'error', text: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleXlsxUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Reset so same file can be re-selected
    event.target.value = '';

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseMcaXlsx(e.target.result);
        if (!parsed.cin && !parsed.name) throw new Error('Could not read company data. Make sure this is an MCA Master Data export.');
        setForm((prev) => ({ ...prev, ...parsed, resolutionAuthorizations: {} }));
        setImportedFileName(file.name);
        setStatus({ type: 'success', text: `Imported from ${file.name} — review and save.` });
      } catch (err) {
        setStatus({ type: 'error', text: err.message || 'Failed to parse file.' });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Data cleaning before sending to backend ──────────────────────────────
  const cleanFormForSubmit = (rawForm) => {
    const cleaned = { ...rawForm };

    // Strip commas from capital/amount fields
    const stripCommas = (v) => v ? String(v).replace(/,/g, '').trim() : '';
    cleaned.authorisedCapital = stripCommas(cleaned.authorisedCapital);
    cleaned.paidUpCapital = stripCommas(cleaned.paidUpCapital);
    cleaned.turnover = stripCommas(cleaned.turnover);

    // Normalize date fields to ISO (backend also does this, but belt-and-suspenders)
    const normDate = (v) => {
      if (!v || v === '-') return '';
      const s = String(v).trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
      if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
      const parsed = new Date(s);
      if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
      return s;
    };
    cleaned.incorporated = normDate(cleaned.incorporated);
    cleaned.lastAgm = normDate(cleaned.lastAgm);
    cleaned.balanceSheetDate = normDate(cleaned.balanceSheetDate);

    // Clean directors
    if (Array.isArray(cleaned.directors)) {
      cleaned.directors = cleaned.directors.map((d) => ({
        ...d,
        appointed: normDate(d.appointed),
        cessation: normDate(d.cessation),
      }));
    }

    // Clean recorded filings — drop rows with no form so a half-entered record
    // never reaches the schema's `form: required`.
    if (Array.isArray(cleaned.filedForms)) {
      cleaned.filedForms = cleaned.filedForms
        .filter((f) => f && f.form)
        .map((f) => ({ ...f, fyEnd: normDate(f.fyEnd), filedOn: normDate(f.filedOn) }));
    }

    // ── Compliance profile ─────────────────────────────────────────────────
    //
    // A blank numeric becomes null, never 0. `parseFloat('') || 0` — the idiom
    // used for capital above — would turn "I don't know the borrowings" into
    // "the borrowings are nil", and a nil answer resolves a threshold instead
    // of leaving it undetermined. numOrNull keeps the two apart.
    const numOrNull = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = parseFloat(String(v).replace(/,/g, '').trim());
      return Number.isFinite(n) ? n : null;
    };

    cleaned.borrowings = numOrNull(cleaned.borrowings);
    cleaned.publicDeposits = numOrNull(cleaned.publicDeposits);
    cleaned.reservesAndSurplus = numOrNull(cleaned.reservesAndSurplus);
    cleaned.foreignShareholdingPercent = numOrNull(cleaned.foreignShareholdingPercent);

    // Dated series. A row with no date cannot be placed on a timeline, so it is
    // dropped rather than saved against an assumed date — `effectiveFrom` is
    // required by the schema and an invented one would be a fabricated fact.
    const cleanSeries = (rows) => (Array.isArray(rows) ? rows : [])
      .filter((r) => r && r.effectiveFrom && r.value !== '' && r.value !== null && r.value !== undefined)
      .map((r) => ({
        value: numOrNull(r.value),
        effectiveFrom: normDate(r.effectiveFrom),
        source: r.source || 'manual',
      }))
      .filter((r) => r.value !== null);

    cleaned.netWorthHistory = cleanSeries(cleaned.netWorthHistory);
    cleaned.netProfitHistory = cleanSeries(cleaned.netProfitHistory);
    cleaned.employeeCountHistory = cleanSeries(cleaned.employeeCountHistory);
    cleaned.esiEligibleCountHistory = cleanSeries(cleaned.esiEligibleCountHistory);

    // `state` is required on the sub-schema — a row without one would be
    // rejected by Mongoose and fail the whole save, so half-entered rows go.
    cleaned.establishments = (Array.isArray(cleaned.establishments) ? cleaned.establishments : [])
      .filter((e) => e && String(e.state || '').trim())
      .map((e) => ({
        ...e,
        employeeCount: numOrNull(e.employeeCount),
        esiEligibleCount: numOrNull(e.esiEligibleCount),
        commencedOn: normDate(e.commencedOn) || null,
        closedOn: normDate(e.closedOn) || null,
        isFactory: Boolean(e.isFactory),
      }));

    cleaned.statutoryCoverage = (Array.isArray(cleaned.statutoryCoverage) ? cleaned.statutoryCoverage : [])
      .filter((c) => c && c.statute)
      .map((c) => ({
        ...c,
        coveredFrom: normDate(c.coveredFrom) || null,
        coveredTo: normDate(c.coveredTo) || null,
      }));

    cleaned.gstRegistrations = (Array.isArray(cleaned.gstRegistrations) ? cleaned.gstRegistrations : [])
      .filter((g) => g && String(g.state || '').trim())
      .map((g) => ({
        ...g,
        gstin: String(g.gstin || '').toUpperCase().trim(),
        registeredOn: normDate(g.registeredOn) || null,
        cancelledOn: normDate(g.cancelledOn) || null,
      }));

    cleaned.exchanges = (Array.isArray(cleaned.exchanges) ? cleaned.exchanges : []).filter(Boolean);

    // Clean charges
    if (Array.isArray(cleaned.charges)) {
      cleaned.charges = cleaned.charges.map((c) => ({
        ...c,
        amount: stripCommas(c.amount),
        created: normDate(c.created),
        modified: normDate(c.modified),
        satisfiedDate: normDate(c.satisfiedDate),
      }));
    }

    // Normalize listed field to valid backend enum values
    const validListed = ['Yes', 'No', 'Yes — BSE', 'Yes — NSE', 'Yes — BSE & NSE'];
    if (!validListed.includes(cleaned.listed)) {
      const raw = (cleaned.listed || '').trim();
      if (!raw || /^(n|no|unlisted)$/i.test(raw)) cleaned.listed = 'No';
      else if (/BSE\s*[&]\s*NSE|NSE\s*[&]\s*BSE/i.test(raw)) cleaned.listed = 'Yes — BSE & NSE';
      else if (/BSE/i.test(raw)) cleaned.listed = 'Yes — BSE';
      else if (/NSE/i.test(raw)) cleaned.listed = 'Yes — NSE';
      else cleaned.listed = /^(y|yes|listed)$/i.test(raw) ? 'Yes' : 'No';
    }

    return cleaned;
  };

  const saveCompany = async () => {
    if (saveInFlightRef.current || saving) return;

    const cin = form.cin.trim().toUpperCase();
    const name = form.name.trim().toUpperCase();

    if (!cin || !name) {
      setStatus({ type: 'error', text: 'CIN / LLPIN and Company Name are required.' });
      return;
    }

    // Validate the identifier — the field accepts all three MCA registry formats:
    //   CIN   (companies)          L/U + 5 digits + state + year + class + serial — U74900DL2023PTC123456
    //   LLPIN (LLPs)               3 letters + 4 digits, hyphen optional — ACS-8933 (mirrors backend VALID_LLPIN_RX)
    //   FCRN  (foreign companies)  F + 4-6 digits — F02936
    const cinRegex = /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;
    const llpinRegex = /^[A-Z]{3}-?[0-9]{4}$/;
    const fcrnRegex = /^F[0-9]{4,6}$/;
    if (!cinRegex.test(cin) && !llpinRegex.test(cin) && !fcrnRegex.test(cin)) {
      setStatus({ type: 'error', text: 'Invalid format. Expected a CIN (U74900DL2023PTC123456), LLPIN (ACS-8933) or FCRN (F02936).' });
      return;
    }

    // Last overwrite path left: form content that came from MCA (XLSX import, or
    // a live fetch whose CIN was edited afterwards) being saved over a company we
    // already store. That save is a blanket PUT of the whole form, so anything
    // blank here — jurisdiction, auditor, notes — would be written as empty over
    // real data. Manual typing is untouched by this guard (no import stamp), and
    // the import isn't blocked: it takes one explicit second click.
    if (!isEditMode && importedFileName) {
      const route = resolveMcaFetchRoute(cin, companies);
      if (route.mode === 'refresh' && overwriteConfirmedRef.current !== cin) {
        overwriteConfirmedRef.current = cin;
        setStatus({
          type: 'warning',
          text: `${route.company?.name || cin} is already in your portfolio. Saving replaces its stored details with exactly what this form shows, and any field left blank here will be cleared. To update only the registry fields — leaving auditor, notes, contacts and jurisdiction alone — use "Fetch from MCA" instead. Click "Add Company" again to overwrite.`,
        });
        return;
      }
    }

    saveInFlightRef.current = true;
    setSaving(true);
    setStatus({ type: '', text: '' });

    try {
      const company = cleanFormForSubmit({
        ...form,
        cin,
        name,
        status: form.status || 'Active',
      });

      // Compute compliance deadlines to populate nextDue and score
      try {
        const cd = computeCompanyDeadlines(company);
        if (cd) {
          const allItems = [...cd.deadlines, ...(cd.agmDeadline ? [cd.agmDeadline] : [])];
          const compliantCount = allItems.filter((d) => d.status === 'compliant').length;
          company.score = allItems.length > 0 ? Math.round((compliantCount / allItems.length) * 100) : 0;
          company.nextDue = cd.nextCritical ? `${cd.nextCritical.code} ${cd.nextCritical.dueDateFormatted}` : 'Board Meeting';
        }
      } catch { /* engine computation is best-effort */ }

      const { outcome } = await addOrUpdateCompany(company);

      if (isEditMode) {
        showToast(`${name} updated successfully`, 'success');
      } else if (outcome === 'claimed') {
        selectCompany(company);
        showToast(`Claimed ${name}. The professional who added it keeps access.`, 'success');
      } else if (outcome === 'access_requested') {
        // No access yet — owned by someone else; don't make it the active company.
        showToast(`${name} is already owned. An access request was sent to the owner — track it on your dashboard or under Collaboration Hub → Clients & Requests.`, 'info');
      } else if (outcome === 'already_has_access') {
        showToast(`You already have access to ${name} (or a request is pending).`, 'info');
      } else {
        selectCompany(company);
        showToast(`Added ${name} to portfolio`, 'success');
      }

      // Saved successfully — clear the form so the next "Add Company" is fresh.
      setForm(INITIAL_FORM);
      formFromEditRef.current = false;
      onClose();
    } catch (error) {
      console.error('Error saving company:', error);

      // Extract specific field errors from backend validation response
      let errorMessage = 'Failed to save company. Please try again.';
      const details = error?.details;

      if (Array.isArray(details) && details.length > 0) {
        // Build a human-readable list from express-validator errors
        const fieldErrors = details.map((e) => {
          const field = e.path || e.param || '';
          const msg = e.msg || e.message || '';
          // Make field names readable
          const labels = {
            cin: 'CIN', name: 'Company Name', incorporated: 'Incorporation Date',
            authorisedCapital: 'Authorised Capital', paidUpCapital: 'Paid-up Capital',
            lastAgm: 'Last AGM Date', balanceSheetDate: 'Balance Sheet Date',
            email: 'Email', type: 'Company Type', status: 'Status',
          };
          const label = labels[field] || field;
          return `${label}: ${msg}`;
        });
        errorMessage = fieldErrors.join('\n');
      } else if (error.code === 'INSUFFICIENT_COMPANY_ACCESS') {
        // The backend already phrased this for the user ("shared with you, not
        // owned by you — ask X to grant edit access"). Surface it verbatim
        // rather than routing it through the CIN/validation guesswork below.
        errorMessage = error.message;
      } else if (error.message || error.error) {
        const fullError = error.error || error.message || '';
        if (fullError.includes('CIN already exists') || error.message?.includes('CIN already exists')) {
          errorMessage = 'A company with this CIN already exists in your portfolio.';
        } else if (fullError.includes('validation failed')) {
          // Parse Mongoose validation: "Company validation failed: field1: msg1, field2: msg2"
          const fieldPart = fullError.split('validation failed:')[1];
          if (fieldPart) {
            const fieldLabels = {
              listed: 'Listing Status', cin: 'CIN', name: 'Company Name',
              incorporated: 'Incorporation Date', authorisedCapital: 'Authorised Capital',
              paidUpCapital: 'Paid-up Capital', lastAgm: 'Last AGM Date',
              balanceSheetDate: 'Balance Sheet Date', email: 'Email',
              type: 'Company Type', status: 'Status',
            };
            const parts = fieldPart.split(',').map(s => {
              const t = s.trim();
              const idx = t.indexOf(':');
              if (idx > -1) {
                const field = t.slice(0, idx).trim();
                return fieldLabels[field] || field;
              }
              return t;
            });
            errorMessage = `Invalid values for: ${parts.join(', ')}. Please check and correct these fields.`;
          } else {
            errorMessage = 'Some fields have invalid values. Please review all fields.';
          }
        } else {
          errorMessage = error.message || fullError;
        }
      }

      setStatus({ type: 'error', text: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      saveInFlightRef.current = false;
      setSaving(false);
    }
  };

  const handleModalClose = () => {
    if (saving) return;
    onClose();
  };

  const statusClass =
    status.type === 'error' ? 'bg-[#f2e9e6] border-[#e6d2cb] text-[#9a564a]'
    : status.type === 'success' ? 'bg-[#e8efe5] border-[#d3e2cb] text-[#4a6b3f]'
    : status.type === 'unavailable' ? 'bg-[#f3ebd7] border-[#e7d7ad] text-[#8a6a1f]'
    : 'bg-[#f3ebd7] border-[#e7d7ad] text-[#8a6a1f]';

  const formLabelClass = labelClass;
  const fieldClass = inputClass;

  const companyForm = (
    <>
      {status.text && (
        <div className={`mb-4 border rounded-lg px-3 py-2 text-[12px] ${statusClass}`}>
          {status.text.includes('\n') ? (
            <ul className="list-disc pl-4 flex flex-col gap-[3px]">
              {status.text.split('\n').map((line, i) => <li key={i}>{line}</li>)}
            </ul>
          ) : status.text}
        </div>
      )}

      <div className="mb-3 flex items-center gap-2">
        <div className="text-[18px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Company Details</div>
        <Chip tone="#145f5f">MCA Fields</Chip>
        {!isEditMode && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto group !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#8a6a1f]/[0.08] hover:!text-[#8a6a1f]"
            onClick={() => setForm(INITIAL_FORM)}
            leftIcon={<svg width="11" height="11" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-hover:-rotate-180"><path d="M11.5 7a4.5 4.5 0 1 1-1.32-3.18M11.5 1.5V4H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          >
            Clear Form
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className={formLabelClass}>CIN / LLPIN / FCRN *</div>
          <input value={form.cin} onChange={(e) => setField('cin', e.target.value.toUpperCase())} className={fieldClass} placeholder="U74999MP2021PTC054994" />
        </div>
        <div>
          <div className={formLabelClass}>Registration Number</div>
          <input value={form.registrationNo} onChange={(e) => setField('registrationNo', e.target.value)} className={fieldClass} placeholder="054994" />
        </div>
      </div>

      <div className="mb-3">
        <div className={formLabelClass}>Company Name *</div>
        <input value={form.name} onChange={(e) => setField('name', e.target.value.toUpperCase())} className={fieldClass} placeholder="STELLAR ALPHA GLOBAL MARKETS PRIVATE LIMITED" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <div className={formLabelClass}>Company Type</div>
          <Select
            value={form.type}
            onChange={(v) => setField('type', v)}
            options={['Pvt Ltd', 'Public', 'LLP', 'OPC', 'Sec 8', 'Foreign Company', 'Nidhi Company', 'Producer Company', 'Government Company']}
          />
        </div>
        <div>
          <div className={formLabelClass}>Class of Company</div>
          <Select
            value={form.classOfCompany}
            onChange={(v) => setField('classOfCompany', v)}
            options={['Private', 'Public', 'Private(One Person Company)', 'NA']}
          />
        </div>
        <div>
          <div className={formLabelClass}>Category of Company</div>
          <Select
            value={form.category}
            onChange={(v) => setField('category', v)}
            options={['Company limited by Shares', 'Company limited by Guarantee', 'Company Limited by Guarantee and having Share Capital', 'Unlimited Company']}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className={formLabelClass}>Sub-Category</div>
          <Select
            value={form.subCategory}
            onChange={(v) => setField('subCategory', v)}
            options={['Non-govt company', 'State Govt company', 'Central Govt company', 'Subsidiary of Foreign Company', 'Guarantee and Association comp', 'Nidhi Company']}
          />
        </div>
        <div>
          <div className={formLabelClass}>Date of Incorporation</div>
          <DateField value={toIso(form.incorporated)} onChange={(v) => setField('incorporated', v)} placeholder="Date of incorporation" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <div className={formLabelClass}>Company Status</div>
          <Select
            value={form.status}
            onChange={(v) => setField('status', v)}
            options={['Active', 'Inactive', 'Dormant under Section 455', 'Under Process of Striking Off', 'Strike Off', 'Struck Off', 'Under Liquidation', 'Liquidated', 'Dissolved', 'Under CIRP', 'Converted to LLP', 'Amalgamated', 'Defunct', 'Not Available for eFiling']}
          />
        </div>
        <div>
          <div className={formLabelClass}>ACTIVE Compliance</div>
          <Select
            value={form.activeCompliance}
            onChange={(v) => setField('activeCompliance', v)}
            options={['-', 'ACTIVE Compliant', 'ACTIVE Non-compliant']}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <div className={formLabelClass}>ROC Name</div>
          <input value={form.roc} onChange={(e) => setField('roc', e.target.value)} className={fieldClass} placeholder="ROC Gwalior" />
        </div>
        <div>
          <div className={formLabelClass}>ROC (Name &amp; Office)</div>
          <input value={form.rocOffice} onChange={(e) => setField('rocOffice', e.target.value)} className={fieldClass} placeholder="ROC Gwalior" />
        </div>
        <div>
          <div className={formLabelClass}>RD (Name &amp; Region)</div>
          <input value={form.rd} onChange={(e) => setField('rd', e.target.value)} className={fieldClass} placeholder="RD, North Western Region" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className={formLabelClass}>Jurisdiction</div>
          <input value={form.jurisdiction} onChange={(e) => setField('jurisdiction', e.target.value)} className={fieldClass} placeholder="e.g. Indore, Madhya Pradesh" />
        </div>
        <div>
          <div className={formLabelClass}>Listed in Stock Exchange</div>
          <Select
            value={form.listed}
            onChange={(v) => setField('listed', v)}
            options={['No', 'Yes', 'Yes — BSE', 'Yes — NSE', 'Yes — BSE & NSE']}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className={formLabelClass}>Authorised Capital (₹)</div>
          <input value={form.authorisedCapital} onChange={(e) => setField('authorisedCapital', e.target.value)} className={fieldClass} placeholder="15,00,000" />
        </div>
        <div>
          <div className={formLabelClass}>Paid-Up Capital (₹)</div>
          <input value={form.paidUpCapital} onChange={(e) => setField('paidUpCapital', e.target.value)} className={fieldClass} placeholder="4,00,000" />
        </div>
      </div>

      {/* Turnover is NOT here. It sits in the Financial position section below,
          with net worth and net profit — it is a question-2 figure, MCA does not
          publish it, and the ledger's "Fill" link has to land on the box it
          names. It used to sit in this MCA block, where Fill opened a section
          that did not contain it. */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className={formLabelClass}>Date of Last AGM</div>
          <DateField value={toIso(form.lastAgm)} onChange={(v) => setField('lastAgm', v)} placeholder="Date of last AGM" />
        </div>
        <div>
          <div className={formLabelClass}>Date of Balance Sheet</div>
          <DateField value={toIso(form.balanceSheetDate)} onChange={(v) => setField('balanceSheetDate', v)} placeholder="Balance sheet date" />
        </div>
      </div>

      <div className="mb-3">
        <div className={formLabelClass}>Email ID</div>
        <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className={fieldClass} placeholder="cs@company.in" />
      </div>

      <div className="mb-3">
        <div className={formLabelClass}>Registered Address</div>
        <textarea value={form.registeredAddress} onChange={(e) => setField('registeredAddress', e.target.value)} className={textareaClass} />
      </div>

      <div className="mb-3">
        <div className={formLabelClass}>Address for Books of Account (if different)</div>
        <textarea value={form.booksAddress} onChange={(e) => setField('booksAddress', e.target.value)} className={textareaClass} placeholder="Leave blank if same as registered office" />
      </div>

      <div className="border-t-2 border-[#ddd6c6] pt-3 mt-1 mb-3">
        <div className="text-[18px] font-bold text-[#08111f] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Practice Management Fields
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <div className={formLabelClass}>Assigned CA / CS</div>
            <input value={form.assignedProfessional} onChange={(e) => setField('assignedProfessional', e.target.value)} className={fieldClass} placeholder="CA Priya Sharma" />
          </div>
          <div>
            <div className={formLabelClass}>Client Contact Person</div>
            <input value={form.contactPerson} onChange={(e) => setField('contactPerson', e.target.value)} className={fieldClass} placeholder="Mr. Rajesh Kumar" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <div className={formLabelClass}>Statutory Auditor</div>
            <input value={form.auditor} onChange={(e) => setField('auditor', e.target.value)} className={fieldClass} placeholder="M/s. Sharma & Associates" />
          </div>
          <div>
            <div className={formLabelClass}>Financial Year End</div>
            <Select
              value={form.financialYearEnd}
              onChange={(v) => setField('financialYearEnd', v)}
              options={['31 March', '30 June', '30 September', '31 December']}
            />
          </div>
        </div>
        <div>
          <div className={formLabelClass}>Notes / Remarks</div>
          <textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} className={textareaClass} placeholder="Any special notes or compliance flags..." />
        </div>
      </div>

      {/* ── Directors & Signatories — inline editable table ──────────────── */}
      {(() => {
        const emptyCount = form.directors.filter(d => !d.din && !d.name).length;
        const canAddMore = emptyCount < 2;
        const addDir = () => {
          if (!canAddMore) return;
          setField('directors', [...form.directors, { din: '', name: '', designation: 'Director', category: 'Promoter', appointed: '', cessation: '', signatory: false }]);
        };
        const updateDir = (i, field, val) => setField('directors', form.directors.map((x, idx) => idx === i ? { ...x, [field]: val } : x));
        const removeDir = (i) => {
          const removed = form.directors[i];
          // Clean up resolution authorizations referencing this director (array shape)
          if (removed?.name) {
            const cleaned = { ...form.resolutionAuthorizations };
            let changed = false;
            for (const [resId, authVal] of Object.entries(cleaned)) {
              const arr = Array.isArray(authVal) ? authVal : (authVal?.name ? [authVal] : []);
              const filtered = arr.filter(
                (a) => !(a.name === removed.name && (a.din || '') === (removed.din || ''))
              );
              if (filtered.length !== arr.length) {
                changed = true;
                if (filtered.length === 0) delete cleaned[resId];
                else cleaned[resId] = filtered;
              }
            }
            if (changed) setField('resolutionAuthorizations', cleaned);
          }
          setField('directors', form.directors.filter((_, idx) => idx !== i));
        };

        const cellCls = 'px-2 py-[5px]';
        const inputCls = inputSmClass;

        return (
          <div className="border-t-2 border-[#ddd6c6] pt-3 mt-1 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <button type="button" onClick={() => toggleSection('directors')} className="group flex items-center gap-2 text-left">
                <FiChevronRight size={16} className={`text-[#7a7060] shrink-0 transition-transform duration-200 ${openSections.directors ? 'rotate-90' : ''}`} />
                <div className="text-[18px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Directors &amp; Signatories</div>
                {form.directors.length > 0 && <Chip bg="#e4f5f3" color="#145f5f">{form.directors.length}</Chip>}
              </button>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto group !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#8a6a1f]/[0.08] hover:!text-[#8a6a1f]"
                onClick={addDir}
                disabled={!canAddMore}
                title={!canAddMore ? 'Fill in the existing empty row first' : ''}
                leftIcon={<svg width="11" height="11" viewBox="0 0 12 12" fill="none" className="transition-transform duration-300 group-hover:rotate-90"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
              >
                Add Director
              </Button>
            </div>
            {openSections.directors && (
            <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#ddd6c6' }}>
              <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '98px' }} />
                  <col style={{ width: 'auto' }} />
                  <col style={{ width: '148px' }} />
                  <col style={{ width: '118px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '56px' }} />
                  <col style={{ width: '30px' }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#f8f5ef', borderBottom: '1px solid #ddd6c6' }}>
                    {['DIN / PAN', 'Name', 'Designation', 'Category', 'Appointed', 'Sign.', ''].map((h, i) => (
                      <th key={i} className="text-left px-2 py-[7px] text-[10px] font-bold uppercase tracking-[.7px] text-[#7a7060]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {form.directors.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-5 text-center text-[11.5px] text-[#7a7060]">
                        No directors added yet — click "Add Director" or import from MCA
                      </td>
                    </tr>
                  ) : form.directors.map((d, i) => (
                    <tr key={i} style={{ borderBottom: i < form.directors.length - 1 ? '1px solid #e8e3d8' : 'none', background: i % 2 === 0 ? '#fff' : '#faf9f6' }}>
                      <td className={cellCls}>
                        <input value={d.din} onChange={(e) => updateDir(i, 'din', e.target.value)} className={inputCls} placeholder="12345678" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10.5px' }} />
                      </td>
                      <td className={cellCls}>
                        <input value={d.name} onChange={(e) => updateDir(i, 'name', e.target.value.toUpperCase())} className={inputCls} placeholder="FULL NAME" />
                      </td>
                      <td className={cellCls}>
                        <Select
                          size="sm"
                          value={d.designation}
                          onChange={(v) => updateDir(i, 'designation', v)}
                          options={['Director', 'Managing Director', 'Whole-time Director', 'Additional Director', 'Alternate Director', 'Nominee Director', 'Independent Director', 'Small Shareholders Director', 'Company Secretary', 'Chief Executive Officer (CEO)', 'Chief Financial Officer (CFO)', 'Manager', 'Designated Partner']}
                        />
                      </td>
                      <td className={cellCls}>
                        <Select
                          size="sm"
                          value={d.category}
                          onChange={(v) => updateDir(i, 'category', v)}
                          options={['Promoter', 'Professional', 'Independent', 'Nominee', 'Executive', 'Non-Executive']}
                        />
                      </td>
                      <td className={cellCls}>
                        <DateField
                          size="sm"
                          value={toIso(d.appointed)}
                          onChange={(v) => updateDir(i, 'appointed', v)}
                          placeholder="Appointed"
                        />
                      </td>
                      <td className={cellCls}>
                        <div className="flex justify-center">
                          <input
                            type="checkbox"
                            checked={Boolean(d.signatory)}
                            onChange={(e) => updateDir(i, 'signatory', e.target.checked)}
                            className="w-4 h-4 accent-[#145f5f] cursor-pointer"
                            title="Mark as signatory for document signatures"
                          />
                        </div>
                      </td>
                      <td className={cellCls}>
                        <button
                          onClick={() => removeDir(i)}
                          className="flex items-center justify-center w-[26px] h-[26px] rounded-md transition-colors"
                          style={{ color: '#7a7060' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#f3ebd7'; e.currentTarget.style.color = '#8a6a1f'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#7a7060'; }}
                          title="Remove director"
                        >
                          <IconTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        );
      })()}

      {/* ── Index of Charges — editable cards ───────────────────────────── */}
      {(() => {
        const updateChg = (i, field, val) => setField('charges', form.charges.map((x, idx) => idx === i ? { ...x, [field]: val } : x));
        const removeChg = (i) => setField('charges', form.charges.filter((_, idx) => idx !== i));
        const addChg = () => setField('charges', [...form.charges, { chargeId: '', srn: '', holderName: '', created: '', modified: '', satisfiedDate: '', isSatisfied: false, amount: '', address: '', registeredByOtherEntity: '', assetHolderName: '' }]);
        const hasOutstanding = form.charges.some(c => !c.isSatisfied);

        const inp = inputSmClass;
        const lbl = 'text-[9.5px] font-bold uppercase tracking-[.7px] text-[#7a7060] mb-[3px]';

        return (
          <div className="border-t-2 border-[#ddd6c6] pt-3 mt-1 mb-3">
            <div className="flex items-center gap-2 mb-3">
              <button type="button" onClick={() => toggleSection('charges')} className="group flex items-center gap-2 text-left">
                <FiChevronRight size={16} className={`text-[#7a7060] shrink-0 transition-transform duration-200 ${openSections.charges ? 'rotate-90' : ''}`} />
                <div className="text-[18px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Index of Charges</div>
                {form.charges.length > 0 && (
                  <Chip bg={hasOutstanding ? '#f3ebd7' : '#e8efe5'} color={hasOutstanding ? '#8a6a1f' : '#4a6b3f'}>
                    {form.charges.filter(c => !c.isSatisfied).length} outstanding
                  </Chip>
                )}
                <span className="text-[10px] text-[#7a7060]">Sec 85 · Companies Act 2013</span>
              </button>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto group !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#8a6a1f]/[0.08] hover:!text-[#8a6a1f]"
                onClick={addChg}
                leftIcon={<svg width="11" height="11" viewBox="0 0 12 12" fill="none" className="transition-transform duration-300 group-hover:rotate-90"><path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>}
              >
                Add Charge
              </Button>
            </div>

            {openSections.charges && (form.charges.length === 0 ? (
              <div className="text-center py-4 rounded-xl border border-dashed text-[12px] text-[#7a7060]" style={{ borderColor: '#ddd6c6' }}>
                No charges registered — import from MCA Master Data or add manually
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {form.charges.map((c, i) => (
                  <div
                    key={i}
                    className="rounded-xl border p-3"
                    style={{ borderColor: c.isSatisfied ? '#ddd6c6' : '#ebdcb9', background: c.isSatisfied ? '#fafaf8' : '#fdf8ee' }}
                  >
                    {/* Card header — IDs + status + remove */}
                    <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                      <span className="text-[10.5px] font-semibold px-2 py-[2px] rounded" style={{ background: '#e8e3d8', color: '#3a3020', fontFamily: "'JetBrains Mono', monospace" }}>
                        ID: {c.chargeId || '—'}
                      </span>
                      {c.srn && (
                        <span className="text-[10.5px] font-semibold px-2 py-[2px] rounded" style={{ background: '#e8e3d8', color: '#3a3020', fontFamily: "'JetBrains Mono', monospace" }}>
                          SRN: {c.srn}
                        </span>
                      )}
                      <Chip className="ml-auto" bg={c.isSatisfied ? '#e8efe5' : '#f3ebd7'} color={c.isSatisfied ? '#4a6b3f' : '#8a6a1f'}>
                        {c.isSatisfied ? 'Satisfied' : 'Outstanding'}
                      </Chip>
                      <button
                        onClick={() => removeChg(i)}
                        className="flex items-center justify-center w-[26px] h-[26px] rounded-md"
                        style={{ color: '#7a7060' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f3ebd7'; e.currentTarget.style.color = '#8a6a1f'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#7a7060'; }}
                        title="Remove charge"
                      >
                        <IconTrash />
                      </button>
                    </div>

                    {/* Row 1 — IDs + holder + amount */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                      <div>
                        <div className={lbl}>Charge ID</div>
                        <input value={c.chargeId} onChange={(e) => updateChg(i, 'chargeId', e.target.value)} className={inp} placeholder="101202362" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
                      </div>
                      <div>
                        <div className={lbl}>SRN</div>
                        <input value={c.srn} onChange={(e) => updateChg(i, 'srn', e.target.value)} className={inp} placeholder="AC2384337" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
                      </div>
                      <div className="col-span-2 md:col-span-1">
                        <div className={lbl}>Charge Holder Name</div>
                        <input value={c.holderName} onChange={(e) => updateChg(i, 'holderName', e.target.value)} className={inp} placeholder="Bank / FI name" />
                      </div>
                      <div>
                        <div className={lbl}>Amount (₹)</div>
                        <input value={c.amount} onChange={(e) => updateChg(i, 'amount', e.target.value)} className={inp} placeholder="7,50,00,000" />
                      </div>
                    </div>

                    {/* Row 2 — dates */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
                      <div>
                        <div className={lbl}>Date of Creation</div>
                        <DateField size="sm" value={toIso(c.created)} onChange={(v) => updateChg(i, 'created', v)} placeholder="Creation date" />
                      </div>
                      <div>
                        <div className={lbl}>Date of Modification</div>
                        <DateField size="sm" value={toIso(c.modified)} onChange={(v) => updateChg(i, 'modified', v)} placeholder="Modification date" />
                      </div>
                      <div>
                        <div className={lbl}>Date of Satisfaction</div>
                        <DateField
                          size="sm"
                          value={toIso(c.satisfiedDate)}
                          onChange={(v) => { updateChg(i, 'satisfiedDate', v); updateChg(i, 'isSatisfied', Boolean(v)); }}
                          placeholder="Satisfaction date"
                        />
                      </div>
                    </div>

                    {/* Row 3 — extra fields */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div className="md:col-span-2">
                        <div className={lbl}>Charge Holder Address</div>
                        <input value={c.address} onChange={(e) => updateChg(i, 'address', e.target.value)} className={inp} placeholder="Branch address" />
                      </div>
                      <div>
                        <div className={lbl}>Asset Holder Name</div>
                        <input value={c.assetHolderName} onChange={(e) => updateChg(i, 'assetHolderName', e.target.value)} className={inp} placeholder="If different" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Compliance profile — the five questions applicability turns on ─ */}
      <CompanyProfileSections
        form={form}
        setField={setField}
        openSections={openSections}
        toggleSection={toggleSection}
      />

      {/* ── Resolution Authorizations — per-resolution director picker ─── */}
      <ResolutionAuthorizationsSection
        directors={form.directors}
        authorizations={form.resolutionAuthorizations || {}}
        onAuthChange={(updated) => setField('resolutionAuthorizations', updated)}
        expanded={resAuthExpanded}
        onToggleExpanded={() => setResAuthExpanded((v) => !v)}
        expandedCategories={expandedCategories}
        onToggleCategory={(id) => setExpandedCategories((prev) => ({ ...prev, [id]: !prev[id] }))}
      />
    </>
  );

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      title={isEditMode ? 'Edit Company Details' : 'Add Company to Portfolio'}
      size="xl"
      bodyClassName="!p-0 !overflow-hidden max-h-[72vh]"
      footerClassName="justify-between"
      footer={(
        <>
          {isEditMode ? (
            (activeCompany && initialData && (activeCompany.cin || activeCompany._id) === (initialData.cin || initialData._id)) ? (
              <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#145f5f]">
                <FiCheckCircle size={15} /> Active workspace
              </span>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="group !h-[42px] !rounded-xl !text-[#145f5f] hover:!bg-[#145f5f]/[0.08] hover:!text-[#145f5f]"
                onClick={() => selectCompany(initialData)}
                leftIcon={<FiCheckCircle size={15} />}
              >
                Set as active
              </Button>
            )
          ) : (
            <div className="text-[11px] text-[#7a7060]">
              * Required fields · All data stored locally in your portfolio
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="group !h-[42px] !rounded-xl hover:!bg-[#8a6a1f]/[0.08] hover:!text-[#8a6a1f]" onClick={handleModalClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" className="!h-[42px] !rounded-xl" onClick={saveCompany} loading={saving} leftIcon={<FiCheck size={15} />}>
              {saving ? 'Saving...' : isEditMode ? 'Update Company' : 'Add Company'}
            </Button>
          </div>
        </>
      )}
    >
      <div className="h-full flex flex-col">

        {/* Tab bar — only shown in add mode */}
        {!isEditMode && (
          <div className="sticky top-0 z-10 px-[22px] py-3 border-b border-[#ddd6c6] bg-[#f8f5ef] flex items-center gap-2.5 flex-shrink-0">
            <div className="flex gap-1 rounded-xl border border-[#ddd6c6] p-1 bg-white">
              {[
                { id: 'add', label: 'Add Company', icon: <FiEdit3 size={13} /> },
                { id: 'fetch', label: 'Fetch from MCA', icon: <FiSearch size={13} /> },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group flex items-center gap-1.5 px-[14px] py-[6px] rounded-lg text-[12px] font-semibold transition-all duration-200 active:scale-[0.96] ${
                    activeTab === tab.id ? 'bg-[#145f5f] text-white shadow-sm' : 'text-[#7a7060] hover:text-[#145f5f] hover:bg-[#faf7f2]'
                  }`}
                >
                  {tab.icon}
                  <span className="transition-transform duration-150 group-active:translate-y-[0.5px]">{tab.label}</span>
                </button>
              ))}
            </div>
            {importedFileName && (
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#145f5f]">
                <span>✓</span>
                <span>{importedFileName}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto cs-content-scroll px-[22px] py-4">

          {/* Fetch from MCA tab */}
          {!isEditMode && activeTab === 'fetch' && (
            <div className="mb-4">
              <div className="text-[18px] font-bold text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Fetch from MCA21 Portal
              </div>
              <div className="text-[12px] text-[#7a7060] mb-3">
                Enter CIN or LLPIN to auto-populate all company details
              </div>
              <div className="flex gap-2 flex-wrap">
                <input
                  value={cinQuery}
                  onChange={(e) => { setCinQuery(e.target.value.toUpperCase()); setRefreshPrompt(null); }}
                  className={`flex-1 min-w-[240px] ${inputClass}`}
                  placeholder="Enter CIN e.g. U74999MP2021PTC054994"
                />
                <Button variant="primary" size="sm" className="!h-[42px] !rounded-xl" onClick={doMcaFetch} loading={loading} leftIcon={<FiSearch size={15} />}>
                  {loading ? 'Fetching…' : 'Fetch Data'}
                </Button>
              </div>
              {/* Already in the portfolio — say what a refresh does before doing it. */}
              {refreshPrompt && (
                <div className="mt-3 rounded-lg px-3 py-3 border" style={{ borderColor: '#e7d7ad', background: '#f3ebd7' }}>
                  <div className="flex items-start gap-2">
                    <span className="shrink-0 mt-[1px] text-[#8a6a1f]"><Icon name="info" size={15} /></span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold text-[#8a6a1f] mb-1">
                        {refreshPrompt.company?.name || refreshPrompt.company?.cin} is already in your portfolio
                      </div>
                      <div className="text-[11px] text-[#7a7060] mb-2">
                        Fetching now updates the registry fields on that record only — name, status, class, category,
                        registration number, capital, ROC, registered address, state and date of incorporation. Your own
                        entries (auditor, notes, client contact, assigned CA / CS) are never touched, and any field MCA
                        doesn&apos;t publish is left exactly as it is. The fields that actually changed are listed once the
                        fetch completes.
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={doMcaRefresh}
                          loading={loading}
                          leftIcon={<Icon name="conversion" size={14} />}
                        >
                          {loading ? 'Refreshing…' : 'Refresh registry fields'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setRefreshPrompt(null)} disabled={loading}>
                          Cancel
                        </Button>
                      </div>
                      <div className="text-[10.5px] text-[#7a7060] mt-2">
                        To change auditor, notes or any other hand-maintained field, open the company from your portfolio
                        and edit it there.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {status.text && status.type !== 'unavailable' && (
                <div className={`mt-3 border rounded-lg px-3 py-2 text-[12px] ${statusClass}`}>
                  {status.text}
                </div>
              )}
              {status.type === 'unavailable' && (
                <div className="mt-3 rounded-lg px-3 py-3 border" style={{ borderColor: '#e7d7ad', background: '#f3ebd7' }}>
                  <div className="text-[12px] font-semibold text-[#8a6a1f] mb-1">MCA data source temporarily unavailable</div>
                  <div className="text-[11px] text-[#7a7060] mb-2">
                    The government data provider has retired this dataset. Use XLSX import instead — it's faster and works offline.
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setActiveTab('add'); setStatus({ type: '', text: '' }); setTimeout(() => uploadInputRef.current?.click(), 100); }}
                    leftIcon={<FiBarChart2 size={15} />}
                  >
                    Switch to XLSX Import
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Add Company tab — upload strip + form */}
          {(isEditMode || activeTab === 'add') && (
            <>
              {/* MCA Import strip — hidden in edit mode */}
              {!isEditMode && (
                <div
                  className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border-[1.5px] cursor-pointer transition-all duration-200 hover:border-[#cdc4b0] active:scale-[0.99]"
                  style={{ borderColor: '#ddd6c6', background: '#f8f5ef' }}
                  onClick={() => uploadInputRef.current?.click()}
                >
                  <span
                    className="shrink-0 grid place-items-center w-9 h-9 rounded-[10px] border"
                    style={{ background: '#e4f5f3', borderColor: 'rgba(20,95,95,0.18)', color: '#145f5f' }}
                  >
                    <FiBarChart2 size={18} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-bold text-[#08111f]">Import from MCA Master Data</div>
                    <div className="text-[11px] text-[#7a7060]">
                      Upload the <span className="font-semibold">AllMDSData.XLSX</span> downloaded from MCA21 portal — all fields auto-filled
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    className="shrink-0 !h-[34px] !rounded-xl"
                    onClick={(e) => { e.stopPropagation(); uploadInputRef.current?.click(); }}
                  >
                    Choose File
                  </Button>
                  <input
                    ref={uploadInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleXlsxUpload}
                  />
                </div>
              )}

              {companyForm}
            </>
          )}

        </div>
      </div>
    </Modal>
  );
}
